// One staff task: fields, status, the linked record, comments and history.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { EmptyState } from '../../components/ui/empty-state'
import { PageFrame } from '../../components/ui/page-frame'
import { PageHeader } from '../../components/ui/page-header'
import { Select } from '../../components/ui/select'
import { AttachmentsPanel } from '../../components/AttachmentsPanel'
import { formatDateTime, formatPlainDate, parsePlainDate } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import {
  addComment,
  deleteTask,
  getTask,
  listAssignees,
  staffTaskKeys,
  StaffTaskApiError,
  updateTask,
  type RepeatRule,
  type TaskActivity,
  type TaskStatus,
} from './api'
import { formatDay, projectRoute, recordRoute, tasksBasePath, type TaskShell } from './lib'
import { AssigneePicker } from './AssigneePicker'
import { CommentBody } from './CommentBody'
import { CommentComposer } from './CommentComposer'
import { SubtaskList } from './SubtaskList'
import { TaskFormDialog } from './TaskFormDialog'
import { PrivateLock } from './ProjectBits'

const SURFACE =
  'rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] p-4 shadow-[var(--surface-lifted-shadow)]'

export function StaffTaskDetail({ shell }: { shell: TaskShell }) {
  const t = useT(staffTasksI18n)
  const { taskId } = useParams({ strict: false }) as { taskId: string }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [comment, setComment] = useState('')
  const base = tasksBasePath(shell)

  const detail = useQuery({ queryKey: staffTaskKeys.detail(taskId), queryFn: () => getTask(taskId) })
  const assignees = useQuery({ queryKey: staffTaskKeys.assignees, queryFn: listAssignees })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })

  const setStatus = useMutation({
    mutationFn: (next: TaskStatus) => updateTask(taskId, { status: next }),
    onSuccess: refresh,
    onError: () => toast.error(t.toasts.error),
  })
  const setAssignees = useMutation({
    mutationFn: (ids: string[]) => updateTask(taskId, { assignee_user_ids: ids }),
    onSuccess: refresh,
    onError: (err) => {
      const code = err instanceof StaffTaskApiError ? err.code : undefined
      toast.error(code === 'assignee_not_in_team' ? t.toasts.notInTeam : t.toasts.error)
    },
  })
  const remove = useMutation({
    mutationFn: () => deleteTask(taskId),
    onSuccess: () => {
      toast.success(t.toasts.deleted)
      refresh()
      void navigate({ to: base })
    },
    onError: () => toast.error(t.toasts.error),
  })
  const sendComment = useMutation({
    mutationFn: () => addComment(taskId, comment.trim()),
    onSuccess: () => {
      setComment('')
      refresh()
    },
    onError: () => toast.error(t.toasts.error),
  })

  const back = (
    <Link
      to={base}
      className="inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t.detail.back}
    </Link>
  )

  if (detail.isError) {
    const missing = detail.error instanceof StaffTaskApiError && detail.error.status === 404
    return (
      <PageFrame>
        {back}
        <EmptyState icon="taskComplete" title={missing ? t.detail.notFound : t.loadError} />
      </PageFrame>
    )
  }
  if (!detail.data) return <PageFrame aria-busy>{back}</PageFrame>

  const { task, subtasks, comments, activity } = detail.data
  const due = task.due_date ? parsePlainDate(task.due_date) : null
  const linkRoute = task.link ? recordRoute(shell, task.link) : null

  return (
    <PageFrame data-testid="staff-task-detail">
      {back}
      <PageHeader
        title={task.title}
        metadata={task.created_by ? t.detail.openedBy(task.created_by.display_name) : undefined}
        actions={
          <>
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              {t.detail.edit}
            </Button>
            {task.can_delete && (
              <Button variant="outlineDestructive" onClick={() => setDeleteOpen(true)}>
                {t.detail.delete}
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="flex min-w-0 flex-col gap-4">
          {task.description && (
            <section className={SURFACE}>
              <p className="m-0 whitespace-pre-wrap text-sm text-[var(--color-text)]">
                {task.description}
              </p>
            </section>
          )}

          <SubtaskList taskId={task.id} subtasks={subtasks} className={SURFACE} />

          <AttachmentsPanel component="staff_task" objectId={task.id} title={t.structure.files} />

          <section className={SURFACE} aria-labelledby="staff-task-comments">
            <h2 id="staff-task-comments" className="m-0 mb-3 text-sm font-semibold">
              {t.detail.commentsTitle}
            </h2>
            {comments.length === 0 ? (
              <p className="m-0 text-sm text-[var(--color-text-muted)]">{t.detail.noComments}</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {comments.map((c) => (
                  <li key={c.id}>
                    <div className="text-xs text-[var(--color-text-muted)]">
                      <span className="font-medium text-[var(--color-text)]">
                        {c.author?.display_name ?? '—'}
                      </span>{' '}
                      · {formatDateTime(c.created_at)}
                    </div>
                    <div className="mt-0.5">
                      <CommentBody body={c.body} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4">
              <CommentComposer
                value={comment}
                onChange={setComment}
                onSubmit={() => sendComment.mutate()}
                people={assignees.data?.items ?? []}
                disabled={sendComment.isPending}
                submitLabel={t.detail.send}
                placeholder={t.detail.commentPlaceholder}
              />
            </div>
          </section>
        </div>

        <aside className="flex flex-col gap-4 max-lg:contents">
          <section className={cn(SURFACE, 'flex flex-col gap-3 text-sm max-lg:order-first')}>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-[var(--color-text-muted)]">{t.columns.status}</span>
              <Select
                value={task.status}
                onChange={(e) => setStatus.mutate(e.target.value as TaskStatus)}
                data-testid="staff-task-status"
              >
                <option value="todo">{t.status.todo}</option>
                <option value="in_progress">{t.status.in_progress}</option>
                <option value="done">{t.status.done}</option>
              </Select>
            </label>
            <Fact label={t.structure.assignees}>
              <AssigneePicker
                people={assignees.data?.items ?? []}
                value={task.assignees.map((person) => person.user_id)}
                onChange={(ids) => setAssignees.mutate(ids)}
                disabled={setAssignees.isPending}
              />
            </Fact>
            <Fact label={t.columns.due}>
              {due ? (
                <span className={cn(task.is_overdue && 'font-medium text-[var(--color-danger)]')}>
                  {formatPlainDate(due)}
                  {task.is_overdue && ` · ${t.overdue}`}
                </span>
              ) : (
                <span className="text-[var(--color-text-muted)]">{t.noDueDate}</span>
              )}
            </Fact>
            <Fact label={t.form.priorityLabel}>
              {task.priority === 'urgent' ? (
                <Badge variant="destructive">{t.priority.urgent}</Badge>
              ) : (
                t.priority.normal
              )}
            </Fact>
            {task.repeat_rule && (
              <Fact label={t.repeat.label}>
                {t.repeat.rules[task.repeat_rule]}
                {task.repeat_until && ` · ${formatDay(task.repeat_until)}`}
              </Fact>
            )}
            {task.meeting_note && (
              <Fact label={t.notes.fromNote}>
                <Link
                  {...projectRoute(shell, task.meeting_note.project_id)}
                  className="hover:underline"
                >
                  {task.meeting_note.title}
                </Link>
              </Fact>
            )}
            {task.project && (
              <Fact label={t.projects.taskField.project}>
                <Link {...projectRoute(shell, task.project.id)} className="hover:underline">
                  {task.project.name}
                  {task.project.is_private && (
                    <PrivateLock className="ml-1 inline h-3 w-3 align-[-1px]" />
                  )}
                </Link>
              </Fact>
            )}
            {task.link && (
              <Fact label={t.form.linkedTo}>
                {linkRoute ? (
                  <Link to={linkRoute.to} params={linkRoute.params} className="hover:underline">
                    {t.linked.kinds[task.link.kind]} · {task.link.label}
                  </Link>
                ) : (
                  `${t.linked.kinds[task.link.kind]} · ${task.link.label}`
                )}
              </Fact>
            )}
          </section>

          <section className={SURFACE} aria-labelledby="staff-task-history">
            <h2 id="staff-task-history" className="m-0 mb-3 text-sm font-semibold">
              {t.detail.historyTitle}
            </h2>
            <ol className="m-0 flex list-none flex-col gap-2 p-0 text-xs">
              {activity.map((a) => (
                <li key={a.id}>
                  <span className="font-medium text-[var(--color-text)]">
                    {a.actor?.display_name ?? '—'}
                  </span>{' '}
                  <span className="text-[var(--color-text-muted)]">{describe(a, t)}</span>
                  <div className="text-[var(--color-text-muted)]">{formatDateTime(a.created_at)}</div>
                </li>
              ))}
            </ol>
          </section>
        </aside>
      </div>

      <TaskFormDialog open={editOpen} onOpenChange={setEditOpen} task={task} />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.detail.deleteConfirmTitle}</DialogTitle>
            <DialogDescription>{t.detail.deleteConfirmBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              {t.form.cancel}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {t.detail.deleteConfirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageFrame>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-[var(--color-text-muted)]">{label}</span>
      <div>{children}</div>
    </div>
  )
}

type Strings = (typeof staffTasksI18n)["en"]

function describe(a: TaskActivity, t: Strings): string {
  const to = typeof a.detail.to === 'string' ? a.detail.to : null
  switch (a.kind) {
    case 'created':
      return t.history.created
    case 'assigned':
      return t.history.assigned(typeof a.detail.to_name === 'string' ? a.detail.to_name : '—')
    case 'unassigned':
      return typeof a.detail.to_name === 'string'
        ? t.history.removedPerson(a.detail.to_name)
        : t.history.unassigned
    case 'status':
      return t.history.status(to && to in t.status ? t.status[to as TaskStatus] : (to ?? ''))
    case 'due_date': {
      const date = to ? parsePlainDate(to) : null
      return t.history.due_date(date ? formatPlainDate(date) : null)
    }
    case 'priority':
      return t.history.priority(to === 'urgent' ? t.priority.urgent : t.priority.normal)
    case 'project':
      return to ? t.history.project(to) : t.history.projectRemoved
    case 'repeat':
      return t.history.repeat(to && to in t.repeat.rules ? t.repeat.rules[to as RepeatRule] : null)
    case 'repeat_next':
      return t.history.repeatNext(formatDay(to) ?? '')
    case 'deleted':
      return t.history.deleted
    default:
      return t.history.edited
  }
}
